/**
 * LitPortalPage — Route-aware LitPortal (formerly LitBridge)
 *
 * Root `/litportal`: embedded federated search (LitPortal) app.
 * Digest/article subroutes: legacy profile/article shells (optional).
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { useAuth } from '../contexts/AuthContext';
import { preferencesAPI, articlesAPI, libraryAPI, litPortalAPI } from '../api';
import { FEDERATED_SEARCH_APP_URL } from '../apiConfig';
import { SharedDigestFilterBar } from '../components/shared/SharedDigestFilterBar';
import { CrossAppLinks } from '../components/CrossAppLinks';
import { Globe, ArrowLeft, FileText, BookOpen, Info, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

/** Query param and localStorage key LitBridge uses for the shared LitCurrent JWT (`litbridge_auth`). */
const LITBRIDGE_AUTH_STORAGE_KEY = 'litbridge_auth';

const LitPortalPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const routeParams = useParams<{ profileId?: string; pmid?: string }>();
  const navV2Enabled = useFeatureFlag('enable_navigation_v2');
  const { isAuthenticated, token } = useAuth();
  const consumedHandoffRef = useRef<string | null>(null);

  const federatedIframeSrc = useMemo(() => {
    try {
      const url = new URL(
        FEDERATED_SEARCH_APP_URL.includes('://')
          ? FEDERATED_SEARCH_APP_URL
          : `https://${FEDERATED_SEARCH_APP_URL}`
      );
      if (!url.pathname || url.pathname === '') url.pathname = '/';
      if (token) url.searchParams.set(LITBRIDGE_AUTH_STORAGE_KEY, token);
      else url.searchParams.delete(LITBRIDGE_AUTH_STORAGE_KEY);
      const pmid = searchParams.get('pmid');
      const q = searchParams.get('q');
      if (pmid) url.searchParams.set('pmid', pmid);
      else url.searchParams.delete('pmid');
      if (q) url.searchParams.set('q', q);
      else url.searchParams.delete('q');
      return url.toString();
    } catch {
      if (!token) return FEDERATED_SEARCH_APP_URL;
      const join = FEDERATED_SEARCH_APP_URL.includes('?') ? '&' : '?';
      return `${FEDERATED_SEARCH_APP_URL}${join}${LITBRIDGE_AUTH_STORAGE_KEY}=${encodeURIComponent(token)}`;
    }
  }, [token, searchParams]);

  const isDigestRoute = navV2Enabled && location.pathname.includes('/litportal/digests/');
  const isArticleRoute = navV2Enabled && location.pathname.includes('/litportal/articles/');
  const isRootRoute = !isDigestRoute && !isArticleRoute;
  const handoffToken =
    searchParams.get('handoff') ||
    searchParams.get('handoff_token') ||
    searchParams.get('token');

  // Profile context
  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null);
  const [profileLabel, setProfileLabel] = useState<string>('');

  // Article data (for article route)
  const [article, setArticle] = useState<any>(null);
  const [articleLoading, setArticleLoading] = useState(false);
  const [articleNotFound, setArticleNotFound] = useState(false);
  const [handoffStatus, setHandoffStatus] = useState<string | null>(null);

  // Route-param sync
  useEffect(() => {
    if (!navV2Enabled) return;
    if (isDigestRoute && routeParams.profileId) {
      setSelectedProfileId(routeParams.profileId);
    } else if (isArticleRoute) {
      const qpProfileId = searchParams.get('profileId');
      if (qpProfileId) setSelectedProfileId(qpProfileId);
    } else if (!isDigestRoute && !isArticleRoute) {
      // Root /litportal — clear profile filter for correct chip state
      setSelectedProfileId(null);
    }
  }, [navV2Enabled, isDigestRoute, isArticleRoute, routeParams.profileId, location.pathname, searchParams]);

  // Resolve profile label from screen-stats
  useEffect(() => {
    if (!selectedProfileId) { setProfileLabel(''); return; }
    (async () => {
      try {
        const stats = await preferencesAPI.getProfilesScreenStats();
        const found = (stats.profiles || []).find((p: any) => p.profile_id === selectedProfileId);
        setProfileLabel(found?.specialty_label || selectedProfileId);
      } catch { setProfileLabel(selectedProfileId); }
    })();
  }, [selectedProfileId]);

  // Load article data for article route
  useEffect(() => {
    if (!isArticleRoute || !routeParams.pmid) {
      setArticle(null);
      setArticleNotFound(false);
      return;
    }
    setArticleLoading(true);
    setArticleNotFound(false);
    (async () => {
      try {
        const data = await articlesAPI.getDetail(routeParams.pmid!);
        if (data) {
          setArticle(data);
        } else {
          setArticleNotFound(true);
        }
      } catch {
        setArticleNotFound(true);
      }
      setArticleLoading(false);
    })();
  }, [isArticleRoute, routeParams.pmid, location.pathname]);

  // Anonymous LitPortal landing can hand selected records to LitCurrent after auth.
  useEffect(() => {
    if (!isRootRoute || !isAuthenticated || !handoffToken) return;
    if (consumedHandoffRef.current === handoffToken) return;

    consumedHandoffRef.current = handoffToken;
    setHandoffStatus('Saving selected LitPortal records to your library...');

    (async () => {
      try {
        const payload = await litPortalAPI.consumeHandoff(handoffToken);

        if (payload.intent === 'save') {
          const records = payload.selected_records || [];
          await Promise.all(records.map((record) =>
            libraryAPI.saveArticle({
              ...record,
              folder: 'Inbox',
              full_text_status: record.full_text_status || 'unknown',
              selected: true,
              source: 'litportal',
            })
          ));

          setHandoffStatus(`Saved ${records.length} LitPortal record${records.length === 1 ? '' : 's'} to your library.`);
          toast.success('LitPortal selection saved to LitHub');
        } else {
          setHandoffStatus('LitPortal handoff received.');
        }

        const next = new URLSearchParams(searchParams);
        next.delete('handoff');
        next.delete('handoff_token');
        next.delete('token');
        navigate({ pathname: location.pathname, search: next.toString() }, { replace: true });
      } catch (error: any) {
        const status = error?.response?.status;
        const message =
          status === 410
            ? 'This LitPortal handoff has expired. Please return to LitPortal and try again.'
            : status === 404
              ? 'This LitPortal handoff was already used or could not be found.'
              : 'Could not complete the LitPortal handoff.';
        setHandoffStatus(message);
        toast.error(message);
      }
    })();
  }, [handoffToken, isAuthenticated, isRootRoute, location.pathname, navigate, searchParams]);

  // Route-aware chip handler
  const handleProfileSelect = useCallback((id: string | null) => {
    if (navV2Enabled) {
      navigate(id ? `/litportal/digests/${id}` : '/litportal');
    } else {
      setSelectedProfileId(id);
    }
  }, [navV2Enabled, navigate]);

  return (
    <div className="flex flex-col flex-1 min-h-0" data-testid="litportal-page">
      {!isRootRoute && (
        <SharedDigestFilterBar
          selectedProfileId={selectedProfileId}
          onSelectProfile={handleProfileSelect}
          showUnscreenedBadge={false}
          showSavedPercent={false}
          showPrimaryStar={true}
          allLabel="All Specialties"
        />
      )}

      {isRootRoute ? (
        <div className="flex flex-col flex-1 min-h-0 w-full bg-white" data-testid="litportal-root">
          {handoffStatus && (
            <div className="shrink-0 px-4 py-2 bg-teal-50 border-b border-teal-200 text-sm text-teal-800">
              {handoffStatus}
            </div>
          )}
          <iframe
            key={token ? 'litportal-fed-auth' : 'litportal-fed-anon'}
            title="LitPortal — Federated search"
            src={federatedIframeSrc}
            className="w-full border-0 block shrink-0 h-[calc(100dvh-var(--litpulse-chrome-height,10rem))] min-h-[min(480px,calc(100dvh-var(--litpulse-chrome-height,10rem)))]"
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
          />
        </div>
      ) : (
        <div className="max-w-3xl mx-auto px-4 py-8 w-full">
          <button
            type="button"
            onClick={() => navigate('/litportal')}
            className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-6"
          >
            <ArrowLeft className="w-4 h-4" /> Back to LitPortal
          </button>

          {/* ═══ DIGEST-DETAIL MODE ═══ */}
        {isDigestRoute && routeParams.profileId && (
          <div data-testid="litportal-digest-detail">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-teal-100 flex items-center justify-center">
                <Globe className="w-5 h-5 text-teal-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-900">LitPortal</h1>
                <p className="text-sm text-slate-500">{profileLabel || 'Loading...'}</p>
              </div>
            </div>
            <div className="p-5 bg-white rounded-xl border border-slate-200 shadow-sm">
              <h2 className="font-semibold text-slate-800 mb-2">Full-Text Import for {profileLabel || 'this specialty'}</h2>
              <p className="text-sm text-slate-600 mb-4">
                When LitPortal is fully integrated, you&apos;ll be able to import full-text articles
                for your {profileLabel || ''} literature queue. Full text enables deeper AI analysis
                in LitScholar and richer audio summaries in LitStream.
              </p>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
                <Info className="w-3.5 h-3.5 shrink-0" />
                Pending platform merge. Coming soon.
              </div>
            </div>
          </div>
        )}

        {/* ═══ ARTICLE-CENTERED MODE ═══ */}
        {isArticleRoute && routeParams.pmid && (
          <div data-testid="litportal-article-detail">
            {articleLoading ? (
              <div className="text-center py-12 text-slate-400">Loading article...</div>
            ) : articleNotFound ? (
              <div className="text-center py-12" data-testid="litportal-article-not-found">
                <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <h3 className="font-medium text-slate-800 mb-1">Article not found in LitCurrent yet.</h3>
                <p className="text-sm text-slate-500 mb-3">PMID {routeParams.pmid}</p>
                <button
                  type="button"
                  onClick={() => navigate(selectedProfileId ? `/litportal/digests/${selectedProfileId}` : '/litportal')}
                  className="text-sm text-sky-600 hover:text-sky-700 font-medium"
                >
                  ← Back to LitPortal
                </button>
              </div>
            ) : article ? (
              <div>
                <div className="p-5 bg-white rounded-xl border border-slate-200 shadow-sm mb-4">
                  <p className="text-xs text-slate-400 uppercase tracking-wide mb-1">PMID {article.pmid}</p>
                  <h2 className="responsive-title font-bold text-slate-900 mb-2">{article.title}</h2>
                  <p className="text-xs text-slate-500 mb-3">
                    {article.journal && <span>{article.journal}</span>}
                    {article.pub_date && <span> · {article.pub_date}</span>}
                  </p>

                  {/* Full-text status */}
                  {article.full_text && article.full_text.length > 50 ? (
                    <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-700 mb-3">
                      <BookOpen className="w-4 h-4 shrink-0" />
                      Full text available ({Math.round(article.full_text.length / 1000)}k chars)
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700 mb-3">
                      <Info className="w-4 h-4 shrink-0" />
                      Abstract available. Full-text import coming soon.
                    </div>
                  )}

                  {/* Abstract */}
                  {article.abstract && (
                    <div className="mt-3">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Abstract</p>
                      <p className="text-sm text-slate-700 leading-relaxed">{article.abstract}</p>
                    </div>
                  )}

                  {/* PubMed link */}
                  <a href={`https://pubmed.ncbi.nlm.nih.gov/${article.pmid}/`} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 mt-3 text-xs text-sky-600 hover:text-sky-700">
                    View on PubMed <ExternalLink className="w-3 h-3" />
                  </a>
                </div>

                {/* CrossAppLinks */}
                <CrossAppLinks pmid={article.pmid} profileId={selectedProfileId} originApp="litportal" />
              </div>
            ) : null}
          </div>
        )}
        </div>
      )}
    </div>
  );
};

export default LitPortalPage;
